import Point from "@arcgis/core/geometry/Point";
import Graphic from "esri/Graphic";
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import styled from "styled-components";
import FilledButton, {
  FilledButtonAlt,
} from "../../components/atoms/FilledButton";
import Dropdown from "../../components/atoms/Dropdown";
import Callout from "../../components/molecules/Callout";
import {
  useFeaturesContext,
  useFeaturesDispatchContext,
  useMapContext,
  useNvrToolModeContext,
  useUploadTypeContext,
} from "../../context/ContextProvider";
import { IState } from "../../context/featuresReducer";
import colours from "../../lib/colours";
import {
  cmTypeHelp,
  defaultTreeBenchmarkCm,
  noScatteredTreeAllowed,
  scatteredTreeOffsetBenchmarkThreshold,
  sizesHelp,
} from "../../lib/constants";
import {
  Patch,
  Tree,
  featureSource,
  nvrToolModes,
  nvrTools,
  treeCmType,
  treeSize,
  treeType,
  uploadTypes,
} from "../../lib/types";
import {
  filterBenchmarksBasedOnConditionScore,
  getLTBenchmarks,
  getLTBenchmarksFromEvc,
  getOverlappingFeatures,
} from "../../utils/featureUtils";
import Badge from "../../components/atoms/Badge";
import Map from "@arcgis/core/Map";
import SpinnerBase from "../../components/atoms/SpinnerBase";
import mapConfig from "../../lib/mapConfig";
import { UnitSpan } from "../../components/atoms/Text";
import { TextInput } from "../../components/atoms/TextInput";
import {
  StyledRadioContainer,
  StyledRadioInput,
  StyledRadioLabel,
} from "../../components/molecules/RadioButtons";
import { checkTreeSizeAboveThreshold } from "../../utils/validations";
import { validateDetailedUpload } from "../../utils/requests";
import validationMessages from "../../lib/detailedValidationMessages";

const Container = styled.div`
  color: ${colours.th1};
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const Title = styled.h3`
  margin-top: 4px;
  font-size: 20px;
  font-weight: 600;
`;

const Subtitle = styled.h4`
  margin-top: 40px;
  margin-bottom: 0px;
  font-weight: 600;
`;

const SubtitleNoMargin = styled(Subtitle)`
  margin-top: 0;
`;

const StyledContentContainer = styled.div`
  text-align: left;
  overflow: auto;
  padding-block: 20px;
  width: 100%;
`;

const StyledProgressContainer = styled.div`
  display: flex;
  align-items: center;
  padding-block: 12px;
  color: ${colours.th1l1};
`;

const StyledSpeciesContainer = styled.div`
  padding-block: 12px;
`;

const StyledSpeciesContainerUnpadded = styled.div`
  padding-block: 0;
`;

const StyledRadioGroupContainer = styled.div`
  margin-block: 12px;
`;

const StyledBadgeContainer = styled.div`
  margin-left: 8px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: 4px;
`;

const SizeHintContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 12px;
`;

const HintText = styled.span`
  font-style: italic;
  color: ${colours.grey1};
`;

const DetailedValidationIssuesList = styled.ol`
  margin: 0;
  padding-left: 20px;
`;

const DetailedValidationIssuesListItem = styled.li`
  white-space: pre-line;
  margin-block: 12px;
`;

const StyledInput = styled(TextInput)`
  width: 106px;
`;

const StyledFooterContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: row-reverse;
`;

const spinnerStyle = {
  display: "flex",
  alignItems: "center",
  marginLeft: "6px",
};

const spinnerChildrenStyle = {
  width: "8px",
  height: "8px",
};

const cmTypeText = {
  [treeCmType.CIR]: "Circumference",
  [treeCmType.DBH]: "Diameter at Breast Height",
};

interface Benchmark {
  name?: string;
  cm: number;
}

interface IProps {
  onClose: any;
  onSubmit: any;
  tree?: Tree;
  graphic?: Graphic;
}

const tableConfig = mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark;

const TreeProperties = ({ onClose, onSubmit, tree, graphic }: IProps) => {
  const { nvrToolMode }: { nvrToolMode: nvrToolModes } =
    useNvrToolModeContext();
  const { uploadType } = useUploadTypeContext();
  const { map }: { map: Map } = useMapContext();
  const state: IState = useFeaturesContext();
  const dispatch = useFeaturesDispatchContext();
  const [type, setType] = useState<treeType>(treeType.Scattered);
  const [allowScattered, setAllowScattered] = useState<boolean>(true);
  const [benchmarkOptions, setBenchmarkOptions] = useState<any[]>();
  const [benchmark, setBenchmark] = useState<Benchmark>();
  const [cmType, setCmType] = useState<treeCmType>(treeCmType.CIR);
  const [cm, setCm] = useState<string>("");
  const [cmValid, setCmValid] = useState<boolean>(true);
  const [isLargeTree, setIsLargeTree] = useState<boolean>(false);
  const [offsetBenchmarkThreshold, setOffsetBenchmarkThreshold] =
    useState<number>(scatteredTreeOffsetBenchmarkThreshold);
  const [validSimpleOffsetTree, setValidSimpleOffsetTree] =
    useState<boolean>(true);
  const [validDetailedOffsetTree, setValidDetailedOffsetTree] =
    useState<boolean>(true);
  const [detailedValidationErrors, setDetailedValidationErrors] = useState<
    string[]
  >([]);
  const [
    validSimpleOffsetTreeCalculationPending,
    setValidSimpleOffsetTreeCalculationPending,
  ] = useState<boolean>(false);
  const [
    validDetailedOffsetTreeCalculationPending,
    setValidDetailedOffsetTreeCalculationPending,
  ] = useState<boolean>(false);
  const [vegCode, setVegCode] = useState<string>();

  const validSimpleOffsetTreeTimeout = useRef<any>();

  const newTree: boolean = !tree;

  const detailedValidationRequired = useCallback(
    () =>
      nvrToolMode === nvrTools.offset &&
      uploadType === uploadTypes.detailed &&
      (tree as Tree).treeType === treeType.Scattered,
    [nvrToolMode, uploadType, tree]
  );

  const findStartingBenchmark = useCallback(
    (benchmarks: any[]) => {
      if (!tree || !tree.benchmark) {
        return benchmarks[0];
      } else {
        return benchmarks.filter(
          (x) => x[tableConfig.speciesField] === tree.benchmark
        )[0];
      }
    },
    [tree]
  );

  const validateDetailedOffsetTree = useCallback(async (tree: Tree) => {
    setValidDetailedOffsetTreeCalculationPending(true);
    const results = await validateDetailedUpload(
      nvrTools.offset,
      [],
      [tree],
      []
    );
    setDetailedValidationErrors(
      results.results[0].value.map((x: any) => validationMessages[x])
    );
    const success = results.results[0].value.length === 0;
    if (!success) {
      setValidDetailedOffsetTree(false);
    } else {
      setValidDetailedOffsetTree(true);
    }
    setValidDetailedOffsetTreeCalculationPending(false);
    return success;
  }, []);

  // initialise form
  useEffect(() => {
    const treeGeom = (tree ? new Point(tree.geom) : graphic?.geometry) as Point;
    let startingTreeType = tree ? tree.treeType : treeType.Scattered;
    const overlappingPatches = getOverlappingFeatures(
      treeGeom as Point,
      state.patchesList.map((x: string) => state.features[x] as Patch)
    );
    if (overlappingPatches.length) {
      startingTreeType = treeType.Patch;
      setAllowScattered(false);
      setOffsetBenchmarkThreshold(1);
    }
    setType(startingTreeType);
    setCmType(tree ? (tree.cmType as treeCmType) : treeCmType.CIR);
    setCm(tree ? String(tree.cm) : "");

    const evcExists =
      !!tree && tree.vegCode && uploadType === uploadTypes.detailed;
    const getBenchmarksPromise = evcExists
      ? getLTBenchmarksFromEvc(tree.vegCode as string)
      : getLTBenchmarks(treeGeom, map);
    getBenchmarksPromise.then((benchmarkData) => {
      if (!benchmarkData || !benchmarkData.evc) {
        setTimeout(() => {
          setBenchmark({
            cm: defaultTreeBenchmarkCm,
          });
        }, 200);
        return;
      }
      let benchmarks = benchmarkData.ltBenchmarks;
      if (tree && nvrToolMode === nvrTools.offset) {
        if (uploadType === uploadTypes.detailed) {
          benchmarks = filterBenchmarksBasedOnConditionScore(benchmarks, tree);
        } else if (uploadType === uploadTypes.simple) {
          const initialBenchmarks = benchmarks;
          benchmarks = benchmarks.filter((x) => {
            const treeCIR =
              tree.cmType === treeCmType.CIR
                ? Number(tree.cm)
                : Number(tree.cm) * Math.PI;
            return checkTreeSizeAboveThreshold(
              treeCIR,
              x.LTBM_NUM,
              offsetBenchmarkThreshold
            );
          });
          if (benchmarks.length === 0) {
            // reset benchmarks list if there are no valid benchmarks to choose from.
            // simple validation of the offset tree will flag the issue
            // maybe this can be handled better in the future.
            benchmarks = initialBenchmarks;
          }
        }
      }

      setTimeout(() => {
        setVegCode(benchmarkData.evc);
        setBenchmarkOptions(benchmarks);
        if (benchmarks && benchmarks.length) {
          const benchmark: any = findStartingBenchmark(benchmarks);
          setBenchmark({
            name: benchmark[tableConfig.speciesField],
            cm: Number(benchmark[tableConfig.benchmarkField]),
          });
          if (detailedValidationRequired()) {
            validateDetailedOffsetTree(tree as Tree);
          }
        }
      }, 200);
    });
  }, [
    state,
    tree,
    graphic,
    map,
    uploadType,
    nvrToolMode,
    findStartingBenchmark,
    detailedValidationRequired,
    validateDetailedOffsetTree,
    offsetBenchmarkThreshold,
  ]);

  // react to any changes to recalculate tree size
  useEffect(() => {
    if (
      !cmValid &&
      nvrToolMode === nvrTools.offset &&
      uploadType === uploadTypes.simple
    ) {
      setValidSimpleOffsetTree(true);
      setValidSimpleOffsetTreeCalculationPending(false);
      if (validSimpleOffsetTreeTimeout.current) {
        clearTimeout(validSimpleOffsetTreeTimeout.current);
      }
    }
    if (benchmark && benchmark.cm && cm && cmValid) {
      const treeCIR =
        cmType === treeCmType.CIR ? Number(cm) : Number(cm) * Math.PI;
      setIsLargeTree(treeCIR >= benchmark.cm);
      if (
        nvrToolMode === nvrTools.offset &&
        uploadType === uploadTypes.simple
      ) {
        if (validSimpleOffsetTreeTimeout.current) {
          clearTimeout(validSimpleOffsetTreeTimeout.current);
        }
        validSimpleOffsetTreeTimeout.current = setTimeout(() => {
          setValidSimpleOffsetTree(
            checkTreeSizeAboveThreshold(
              treeCIR,
              benchmark.cm,
              offsetBenchmarkThreshold
            )
          );
          setValidSimpleOffsetTreeCalculationPending(false);
        }, 1000);
        setValidSimpleOffsetTreeCalculationPending(true);
      }
    }
  }, [
    state,
    uploadType,
    cm,
    cmType,
    benchmark,
    cmValid,
    nvrToolMode,
    offsetBenchmarkThreshold,
    validSimpleOffsetTreeTimeout,
  ]);

  const onTreeTypeChange = (e: any) => {
    setType(e.target.value);
  };

  // validate cm value
  useEffect(() => {
    const isValid = !["", "0"].includes(cm) && Number(cm) > 0;
    setCmValid(isValid);
  }, [cm, setCmValid]);

  const onBenchmarkChange = (e: any) => {
    const name = e.target.value;
    const selectedBenchmark = benchmarkOptions?.filter(
      (x) => x[tableConfig.speciesField] === name
    )[0];
    setBenchmark({
      name: selectedBenchmark[tableConfig.speciesField],
      cm: selectedBenchmark[tableConfig.benchmarkField],
    });
    if (detailedValidationRequired()) {
      const editedTree = { ...tree, ...{ lt_val: name } };
      validateDetailedOffsetTree(editedTree as Tree);
    }
  };

  const onRadioChange = (e: any) => {
    setCmType(e.target.value);
  };

  const onCmChange = (e: any) => {
    setCm(e.target.value);
  };

  const saveTree = () => {
    const updates: any = {
      source: tree?.source || featureSource.Drawn,
      geom: tree ? tree.geom : graphic?.geometry.toJSON(),
      treeType: type,
      cm: Number(cm),
      cmType: cmType,
      size: isLargeTree ? treeSize.Lg : treeSize.Sm,
      vegCode: vegCode,
      benchmark: benchmark?.name,
      benchmarkCm: benchmark?.cm,
    };
    const feature = { ...tree, ...updates };

    if (uploadType === uploadTypes.detailed) {
      feature.lt_val = benchmark?.name;
    }
    dispatch({
      type: tree ? "updateTree" : "addTree",
      payload: feature,
    });
  };

  const onConfirm = () => {
    saveTree();
    onSubmit();
  };

  const title = !!tree ? "Edit Tree " + tree.id : "New Tree";
  const sizeText = isLargeTree ? treeSize.Lg : treeSize.Sm;

  const isFormSubmitDisabled = useMemo(() => {
    if (nvrToolMode === nvrTools.offset) {
      return (
        !cmValid ||
        validSimpleOffsetTreeCalculationPending ||
        !validSimpleOffsetTree ||
        validDetailedOffsetTreeCalculationPending ||
        !validDetailedOffsetTree
      );
    } else {
      return !cmValid;
    }
  }, [
    cmValid,
    nvrToolMode,
    validSimpleOffsetTree,
    validSimpleOffsetTreeCalculationPending,
    validDetailedOffsetTree,
    validDetailedOffsetTreeCalculationPending,
  ]);

  const renderSpeciesControl = () => {
    if (!benchmarkOptions) return;
    const ContainerElement =
      uploadType === uploadTypes.simple
        ? StyledSpeciesContainer
        : StyledSpeciesContainerUnpadded;
    const SubtitleElement =
      uploadType === uploadTypes.simple ? Subtitle : SubtitleNoMargin;
    return (
      <ContainerElement>
        <SubtitleElement>Tree benchmark</SubtitleElement>
        <p style={{ marginBottom: "6px" }}>Select genus/species</p>
        <Dropdown
          value={benchmark?.name}
          onChange={onBenchmarkChange}
          disabled={validDetailedOffsetTreeCalculationPending}
        >
          {benchmarkOptions.map((x: any, index) => (
            <option key={index} value={x[tableConfig.speciesField]}>
              {x[tableConfig.speciesField]}
            </option>
          ))}
        </Dropdown>
      </ContainerElement>
    );
  };

  if (!benchmark)
    return (
      <StyledProgressContainer>
        <span>Fetching benchmark options</span>
        <SpinnerBase
          style={spinnerStyle}
          childrenStyle={spinnerChildrenStyle}
        />
      </StyledProgressContainer>
    );

  return (
    <Container>
      <Title>{title}</Title>
      <StyledContentContainer>
        {uploadType === uploadTypes.simple && (
          <>
            <Subtitle style={{ marginBlock: "0 16px" }}>Tree type</Subtitle>
            <Dropdown
              value={type}
              disabled={!allowScattered || nvrToolMode === nvrTools.offset}
              onChange={onTreeTypeChange}
            >
              {((nvrToolMode === nvrTools.offset && !allowScattered) ||
                nvrToolMode !== nvrTools.offset) && (
                <option value={treeType.Patch}>Patch Tree</option>
              )}
              {allowScattered && (
                <option value={treeType.Scattered}>Scattered Tree</option>
              )}
            </Dropdown>
            {!allowScattered && (
              <div style={{ marginTop: "12px" }}>
                <Callout type="info">{noScatteredTreeAllowed}</Callout>
              </div>
            )}
          </>
        )}

        {benchmarkOptions && benchmarkOptions.length > 1
          ? renderSpeciesControl()
          : null}

        {uploadType === uploadTypes.simple && (
          <>
            <Subtitle>Tree size</Subtitle>
            <StyledRadioGroupContainer>
              <StyledRadioContainer>
                <StyledRadioInput
                  type="radio"
                  id={treeCmType.CIR}
                  name="cmType"
                  value={treeCmType.CIR}
                  onChange={onRadioChange}
                  checked={cmType === treeCmType.CIR}
                />
                <StyledRadioLabel htmlFor={treeCmType.CIR}>
                  {cmTypeText[treeCmType.CIR]}
                  <UnitSpan>(cm)</UnitSpan>
                  <StyledBadgeContainer>
                    <Badge
                      color={colours[treeCmType.CIR]}
                      helpText={cmTypeHelp[treeCmType.CIR]}
                    >
                      {treeCmType.CIR}
                    </Badge>
                  </StyledBadgeContainer>
                </StyledRadioLabel>
              </StyledRadioContainer>
              <StyledRadioContainer>
                <StyledRadioInput
                  type="radio"
                  id={treeCmType.DBH}
                  name="cmType"
                  value={treeCmType.DBH}
                  onChange={onRadioChange}
                  checked={cmType === treeCmType.DBH}
                />
                <StyledRadioLabel htmlFor={treeCmType.DBH}>
                  {cmTypeText[treeCmType.DBH]}
                  <UnitSpan>(cm)</UnitSpan>
                  <StyledBadgeContainer>
                    <Badge
                      color={colours[treeCmType.DBH]}
                      helpText={cmTypeHelp[treeCmType.DBH]}
                    >
                      {treeCmType.DBH}
                    </Badge>
                  </StyledBadgeContainer>
                </StyledRadioLabel>
              </StyledRadioContainer>
            </StyledRadioGroupContainer>

            <InputContainer>
              <StyledInput
                autoFocus={newTree}
                type="number"
                min={1}
                placeholder="Enter cm"
                value={cm}
                onChange={onCmChange}
              ></StyledInput>
              {cmValid &&
                (nvrToolMode === nvrTools.removal ||
                  (nvrToolMode === nvrTools.offset && isLargeTree)) && (
                  <SizeHintContainer>
                    <HintText>
                      {isLargeTree ? "Large Tree" : "Small Tree"}
                    </HintText>
                    <StyledBadgeContainer>
                      <Badge
                        color={colours[sizeText]}
                        helpText={sizesHelp[sizeText]}
                      >
                        {sizeText}
                      </Badge>
                    </StyledBadgeContainer>
                  </SizeHintContainer>
                )}
            </InputContainer>
          </>
        )}

        {nvrToolMode === nvrTools.offset && !validSimpleOffsetTree && (
          <div style={{ marginTop: "16px" }}>
            <Callout type="warning">
              This tree is too small and does not meet the offset requirements
              for{" "}
              {offsetBenchmarkThreshold === 1
                ? "trees in patches"
                : "Scattered Trees"}
              .
            </Callout>
          </div>
        )}

        {validDetailedOffsetTreeCalculationPending && (
          <StyledProgressContainer style={{ paddingBlock: "24px 0" }}>
            <span>Validating tree</span>
            <SpinnerBase
              style={spinnerStyle}
              childrenStyle={spinnerChildrenStyle}
            />
          </StyledProgressContainer>
        )}

        {nvrToolMode === nvrTools.offset && !validDetailedOffsetTree && (
          <div style={{ marginTop: "16px" }}>
            <Callout type="warning">
              The selected genus/species does not meet the following offset
              requirements for Scattered Trees.
              <DetailedValidationIssuesList style={{ margin: 0 }}>
                {detailedValidationErrors.map((x: string, index: number) => (
                  <DetailedValidationIssuesListItem key={index}>
                    {x}
                  </DetailedValidationIssuesListItem>
                ))}
              </DetailedValidationIssuesList>
            </Callout>
          </div>
        )}
      </StyledContentContainer>
      <StyledFooterContainer>
        <FilledButtonAlt onClick={onClose}>Cancel</FilledButtonAlt>
        <FilledButton
          disabled={isFormSubmitDisabled}
          onClick={onConfirm}
          style={{ marginRight: "10px", width: "96px" }}
        >
          {validSimpleOffsetTreeCalculationPending ||
          validDetailedOffsetTreeCalculationPending ? (
            <SpinnerBase
              style={{
                justifyContent: "center",
                width: "auto",
                ...spinnerStyle,
              }}
              childrenStyle={spinnerChildrenStyle}
            />
          ) : (
            "Confirm"
          )}
        </FilledButton>
      </StyledFooterContainer>
    </Container>
  );
};

export default TreeProperties;
